import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Quote, Star, Loader2, Trophy, Crown } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

function PickCard({ pick }) {
  const navigate = useNavigate();
  const p = pick.product;
  const onSale = p.sale_price && p.sale_price < p.price;
  const price = onSale ? p.sale_price : p.price;
  const goToProduct = () =>
    navigate(`/product/${p.id}${pick.influencer_code ? `?ref=${pick.influencer_code}` : ""}`);

  return (
    <div
      data-testid={`pick-card-${pick.id}`}
      className="bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:border-[#E8956B]/60 hover:shadow-[0_14px_34px_rgba(0,0,0,0.08)] transition-all duration-300 flex flex-col"
    >
      <div onClick={goToProduct} className="relative aspect-square overflow-hidden bg-neutral-100 cursor-pointer group">
        <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-[#E8956B] text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider flex items-center gap-1">
          <Star size={11} fill="currentColor" /> PICK
        </span>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span className="w-6 h-6 rounded-full bg-[#E8956B] text-white flex items-center justify-center text-[10px] font-bold">
            {pick.influencer_name?.[0]?.toUpperCase()}
          </span>
          <span className="font-semibold text-neutral-800">{pick.influencer_name}</span>
        </div>
        {pick.note && (
          <p className="mt-3 text-sm text-neutral-600 italic leading-relaxed flex gap-1.5">
            <Quote size={14} className="text-[#E8956B] shrink-0 mt-0.5" />
            <span className="line-clamp-3">{pick.note}</span>
          </p>
        )}
        <div className="mt-3">
          <h3 className="font-semibold text-neutral-900 text-sm leading-tight line-clamp-1">{p.title}</h3>
          <p className="text-xs text-neutral-500">{p.brand}</p>
          {p.seller_name && (
            <button
              data-testid={`pick-seller-link-${pick.id}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/seller/${p.seller_id}`); }}
              className="text-[11px] text-neutral-400 hover:text-[#E8956B] hover:underline transition-colors"
            >
              by {p.seller_name}
            </button>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-lg font-bold text-neutral-900">${price.toFixed(2)}</span>
          {onSale && <span className="text-sm text-neutral-400 line-through">${p.price.toFixed(2)}</span>}
        </div>
        <button
          data-testid={`shop-pick-${pick.id}`}
          onClick={goToProduct}
          className="mt-3 w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-2 text-sm btn-glow transition-all"
        >
          Shop This Pick
        </button>
      </div>
    </div>
  );
}

export default function InfluencerPicks() {
  const [picks, setPicks] = useState(null);
  const [board, setBoard] = useState([]);

  usePageMeta(
    "Influencer Picks — Curated Beauty Recommendations | Beauty Trade Club",
    "Discover makeup and beauty hand-picked by our verified influencers. Shop creator-curated finds and support your favorite beauty creators on Beauty Trade Club.",
    "/picks"
  );

  useEffect(() => {
    api.get("/picks").then(({ data }) => setPicks(data)).catch(() => setPicks([]));
    api.get("/influencers/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);

  const medal = ["text-amber-400", "text-neutral-400", "text-[#CE744A]"];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2 text-[#E8956B] text-sm font-extrabold uppercase tracking-[0.3em] mb-3">
          <Sparkles size={16} /> Curated by creators
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-neutral-900">Picks</h1>
        <p className="mt-3 text-neutral-500 max-w-2xl mx-auto">
          Beauty the community loves — hand-picked by our verified influencers. Every pick is a genuine recommendation
          from a real beauty creator, linking straight to a shoppable listing on the marketplace. Buy through a pick and
          you'll be supporting the creator, who earns a small commission on the products they recommend. Explore their
          favorites below and find your next must-have.
        </p>
      </div>

      {board.length > 0 && (
        <section data-testid="leaderboard" className="mb-12 bg-white border border-neutral-200 rounded-2xl p-6 max-w-3xl mx-auto">
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-4">
            <Trophy size={20} className="text-[#E8956B]" /> Top Influencers
          </h2>
          <div className="space-y-2">
            {board.map((inf, i) => (
              <div key={inf.id} data-testid={`leaderboard-row-${i}`} className="flex items-center gap-3 py-2 border-b border-neutral-100 last:border-0">
                {i < 3 ? <Crown size={18} className={medal[i]} fill="currentColor" /> : <span className="w-[18px] text-center text-sm font-bold text-neutral-400">{i + 1}</span>}
                <span className="w-8 h-8 rounded-full bg-[#E8956B] text-white flex items-center justify-center text-sm font-bold">{inf.name?.[0]?.toUpperCase()}</span>
                <span className="flex-1 font-semibold text-neutral-800">{inf.name}</span>
                <span className="text-sm text-neutral-500">{inf.pick_count} pick{inf.pick_count === 1 ? "" : "s"}</span>
                <span className="text-sm font-bold text-[#E8956B] w-20 text-right">${inf.earnings.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {picks === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#E8956B]" /></div>
      ) : picks.length === 0 ? (
        <p className="text-center text-neutral-400 py-16" data-testid="no-picks">No picks yet — check back soon! ✨</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="picks-grid">
          {picks.map((pick) => <PickCard key={pick.id} pick={pick} />)}
        </div>
      )}
    </div>
  );
}
